import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { ChevronLeft, ChevronRight, DollarSign, Calendar, Car, Check, Search, ArrowLeft, Filter, Send, User, Mail, Phone, MessageSquare, CheckCircle, ArrowUp, ArrowDown, SlidersHorizontal } from 'lucide-react';
import { carData, CarModel, CarType, TrimLevel, getCarImageUrl, getYearsFromRange, estimatePriceForYear, calculateTotalCost } from '../data/carData';
import { DualRangeSlider } from '../components/ui/DualRangeSlider';
interface CarFinderPageProps {
  onBack: () => void;
}
type Step = 'budget' | 'year' | 'type' | 'makes' | 'results' | 'selectYear' | 'trim' | 'contact';
interface FilterState {
  budgetMin: number;
  budgetMax: number;
  yearMin: number;
  yearMax: number;
  types: CarType[];
  makes: string[];
}
interface ContactFormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}
type SortOption = 'price' | 'year';
type SortDirection = 'asc' | 'desc';
const CURRENT_YEAR = new Date().getFullYear();
const MIN_LEGAL_YEAR = CURRENT_YEAR - 8;
const MAX_BUDGET_LIMIT = 40000;
const MIN_BUDGET_LIMIT = 3000;
const INITIAL_FILTERS: FilterState = {
  budgetMin: 5000,
  budgetMax: 15000,
  yearMin: MIN_LEGAL_YEAR,
  yearMax: CURRENT_YEAR,
  types: [],
  makes: []
};
const INITIAL_CONTACT_FORM: ContactFormState = {
  name: '',
  email: '',
  phone: '',
  message: ''
};
const TYPE_OPTIONS: CarType[] = ['Sedán', 'SUV', 'Pick-up', 'Hatchback', 'Deportivo'];
const MAKE_OPTIONS = Array.from(new Set(carData.map(c => c.make))).sort();
const EMAILJS_SERVICE_ID = 'service_m7m0m09';
const EMAILJS_TEMPLATE_ID = 'template_hyqrklh';
const EMAILJS_PUBLIC_KEY = 'w8t9KyY5LzV6mitFs';
export function CarFinderPage({
  onBack
}: CarFinderPageProps) {
  const [step, setStep] = useState<Step>('budget');
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [results, setResults] = useState<CarModel[]>([]);
  const [direction, setDirection] = useState(0);
  // Selection state
  const [selectedCar, setSelectedCar] = useState<CarModel | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedTrim, setSelectedTrim] = useState<TrimLevel | null>(null);
  // Contact form state
  const [contactForm, setContactForm] = useState<ContactFormState>(INITIAL_CONTACT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Sorting state
  const [sortBy, setSortBy] = useState<SortOption>('price');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const handleImageError = (key: string) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Filter and Sort Logic
  useEffect(() => {
    if (step === 'results') {
      let filtered = carData.filter(car => {
        // Budget check: car's cheapest option must be within user's max budget
        // and car's most expensive option must be at or above user's min budget
        const fitsbudget = car.priceRange.min <= filters.budgetMax && car.priceRange.max >= filters.budgetMin;
        // Year overlap check
        const parts = car.yearRange.split('-').map(Number);
        const carStart = parts[0];
        const carEnd = parts[1] || parts[0];
        const yearOverlap = carStart <= filters.yearMax && carEnd >= filters.yearMin;
        const fitsType = filters.types.length === 0 || filters.types.includes(car.type);
        const fitsMake = filters.makes.length === 0 || filters.makes.includes(car.make);
        return fitsbudget && yearOverlap && fitsType && fitsMake;
      });
      // Sorting
      filtered.sort((a, b) => {
        let valA = 0;
        let valB = 0;
        if (sortBy === 'price') {
          valA = (a.priceRange.min + a.priceRange.max) / 2;
          valB = (b.priceRange.min + b.priceRange.max) / 2;
        } else if (sortBy === 'year') {
          const partsA = a.yearRange.split('-').map(Number);
          const partsB = b.yearRange.split('-').map(Number);
          valA = partsA[1] || partsA[0];
          valB = partsB[1] || partsB[0];
        }
        if (sortDirection === 'asc') {
          return valA - valB;
        } else {
          return valB - valA;
        }
      });
      setResults(filtered);
    }
  }, [step, filters, sortBy, sortDirection]);
  const nextStep = () => {
    setDirection(1);
    if (step === 'budget') setStep('year');else if (step === 'year') setStep('type');else if (step === 'type') setStep('makes');else if (step === 'makes') setStep('results');else if (step === 'results' && selectedCar) setStep('selectYear');else if (step === 'selectYear' && selectedYear) setStep('trim');else if (step === 'trim' && selectedTrim) setStep('contact');
  };
  const prevStep = () => {
    setDirection(-1);
    if (step === 'year') setStep('budget');else if (step === 'type') setStep('year');else if (step === 'makes') setStep('type');else if (step === 'results') setStep('makes');else if (step === 'selectYear') {
      setStep('results');
      setSelectedYear(null);
    } else if (step === 'trim') setStep('selectYear');else if (step === 'contact') setStep('trim');
  };
  const toggleType = (type: CarType) => {
    setFilters(prev => ({
      ...prev,
      types: prev.types.includes(type) ? prev.types.filter(t => t !== type) : [...prev.types, type]
    }));
  };
  const toggleMake = (make: string) => {
    setFilters(prev => ({
      ...prev,
      makes: prev.makes.includes(make) ? prev.makes.filter(m => m !== make) : [...prev.makes, make]
    }));
  };
  const handleSelectCar = (car: CarModel) => {
    setSelectedCar(car);
    setSelectedYear(null);
    setSelectedTrim(null);
    setDirection(1);
    setStep('selectYear');
    window.scrollTo(0, 0);
  };
  const handleSelectYear = (year: number) => {
    setSelectedYear(year);
    setDirection(1);
    setStep('trim');
    window.scrollTo(0, 0);
  };
  const handleSelectTrim = (trim: TrimLevel) => {
    setSelectedTrim(trim);
    setDirection(1);
    setStep('contact');
    window.scrollTo(0, 0);
  };
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCar || !selectedTrim || !selectedYear) return;
    setIsSubmitting(true);
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_name: contactForm.name,
        from_email: contactForm.email,
        phone: contactForm.phone,
        message: `
Vehículo de interés: ${selectedYear} ${selectedCar.make} ${selectedCar.model}
Versión seleccionada: ${selectedTrim.name}
Rango de precio estimado: ${selectedTrim.priceEstimate}

Mensaje del cliente:
${contactForm.message || 'Sin mensaje adicional'}
          `,
        vehicle_types: `${selectedYear} ${selectedCar.make} ${selectedCar.model} - ${selectedTrim.name}`,
        budget: selectedTrim.priceEstimate,
        usage: 'Interés específico desde Car Finder'
      }, EMAILJS_PUBLIC_KEY);
      setIsSuccess(true);
    } catch (error) {
      console.error('EmailJS error:', error);
      alert('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const allSteps: Step[] = ['budget', 'year', 'type', 'makes', 'results', 'selectYear', 'trim', 'contact'];
  const renderProgressBar = () => {
    const currentIndex = allSteps.indexOf(step);
    const progress = (currentIndex + 1) / allSteps.length * 100;
    return <div className="w-full bg-gray-200 h-2 rounded-full mb-8 overflow-hidden" data-id="element-408">
        <motion.div className="h-full bg-blue-600" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-409" />
      </div>;
  };
  const getBudgetDescription = (min: number, max: number) => {
    if (max < 8000) return 'Autos económicos, proyectos o primeros vehículos.';
    if (max < 15000) return 'Sedanes confiables y SUVs de años anteriores.';
    if (max < 25000) return 'Modelos recientes, tecnología moderna y mayor seguridad.';
    return 'Vehículos premium, camionetas recientes o deportivos.';
  };
  const renderCarImage = (make: string, model: string, year: number, imageKey: string, className: string) => {
    if (failedImages.has(imageKey)) {
      return <div className={`${className} bg-gray-100 flex items-center justify-center`} data-id="element-410">
          <Car className="w-12 h-12 text-gray-300" data-id="element-411" />
        </div>;
    }
    return <img src={getCarImageUrl(make, model, year)} alt={`${year} ${make} ${model}`} className={`${className} object-cover bg-gray-100`} onError={() => handleImageError(imageKey)} data-id="element-412" />;
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-gray-50 pt-20 pb-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center" data-id="element-413">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-10 text-center border border-gray-100" data-id="element-414">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-415">
            <CheckCircle className="w-10 h-10 text-green-600" data-id="element-416" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-417">
            ¡Solicitud Enviada!
          </h2>
          <p className="text-gray-600 text-lg mb-8" data-id="element-418">
            Hemos recibido tu interés por el{' '}
            <strong data-id="element-419">
              {selectedYear} {selectedCar?.make} {selectedCar?.model}{' '}
              {selectedTrim?.name}
            </strong>
            . Te contactaré pronto para asesorarte con la importación.
          </p>
          <button onClick={() => {
          setIsSuccess(false);
          setStep('budget');
          setFilters(INITIAL_FILTERS);
          setSelectedCar(null);
          setSelectedYear(null);
          setSelectedTrim(null);
          setContactForm(INITIAL_CONTACT_FORM);
        }} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg transition-all" data-id="element-420">
            Buscar otro vehículo
          </button>
        </div>
      </div>;
  }
  const genericHeaderSteps: Step[] = ['budget', 'year', 'type', 'makes'];
  const showGenericHeader = genericHeaderSteps.includes(step);
  return <div className="min-h-screen bg-gray-50 pt-20 pb-12 px-4 sm:px-6 lg:px-8" data-id="element-421">
      <div className="max-w-3xl mx-auto" data-id="element-422">
        <div className="flex items-center mb-8" data-id="element-423">
          <button onClick={onBack} className="flex items-center text-gray-600 hover:text-blue-600 transition-colors" data-id="element-424">
            <ArrowLeft className="w-5 h-5 mr-2" data-id="element-425" />
            Volver al inicio
          </button>
        </div>

        {(step === 'year' || step === 'results') && <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-id="element-426">
            <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-427" />
            <div data-id="element-428">
              <p className="text-sm font-medium text-blue-900" data-id="element-429">
                Ley de importación de El Salvador (8 años)
              </p>
              <p className="text-xs text-blue-700 mt-1" data-id="element-430">
                Solo se pueden importar vehículos con un máximo de 8 años de
                antigüedad. En {CURRENT_YEAR}, el año mínimo permitido es{' '}
                <strong data-id="element-431">{MIN_LEGAL_YEAR}</strong>.
              </p>
            </div>
          </div>}

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100" data-id="element-432">
          <div className="p-6 md:p-10" data-id="element-433">
            {showGenericHeader && <div className="mb-8" data-id="element-434">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-435">
                  Encuentra tu Carro Ideal
                </h1>
                <p className="text-gray-500" data-id="element-436">
                  Responde unas preguntas para encontrar las mejores opciones
                  para ti.
                </p>
                <div className="mt-6" data-id="element-437">{renderProgressBar()}</div>
              </div>}

            {step === 'selectYear' && selectedCar && <div className="mb-8" data-id="element-438">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-439">
                  Selecciona el año del {selectedCar.make} {selectedCar.model}
                </h1>
                <p className="text-gray-500" data-id="element-440">
                  El precio varía según el año del modelo. Los más recientes
                  tienen mayor valor.
                </p>
                <div className="mt-6" data-id="element-441">{renderProgressBar()}</div>
              </div>}

            {step === 'trim' && selectedCar && <div className="mb-8" data-id="element-442">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-443">
                  Elige la versión del {selectedYear} {selectedCar.make}{' '}
                  {selectedCar.model}
                </h1>
                <p className="text-gray-500" data-id="element-444">
                  El precio varía según el equipamiento. Selecciona la que mejor
                  se adapte a tu presupuesto.
                </p>
                <div className="mt-6" data-id="element-445">{renderProgressBar()}</div>
              </div>}

            {step === 'contact' && selectedCar && selectedTrim && <div className="mb-8" data-id="element-446">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-447">
                  ¡Excelente elección!
                </h1>
                <p className="text-gray-500" data-id="element-448">
                  Déjanos tus datos para enviarte una cotización detallada del{' '}
                  <strong data-id="element-449">
                    {selectedYear} {selectedCar.make} {selectedCar.model}{' '}
                    {selectedTrim.name}
                  </strong>
                  .
                </p>
                <div className="mt-6" data-id="element-450">{renderProgressBar()}</div>
              </div>}

            <AnimatePresence custom={direction} mode="wait" data-id="element-451">
              <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} data-id="element-452">
                {/* STEP 1: BUDGET */}
                {step === 'budget' && <div className="space-y-8" data-id="element-453">
                    <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-454">
                      <DollarSign className="text-blue-600" data-id="element-455" />
                      ¿Cuál es tu rango de presupuesto?
                    </h2>
                    <p className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg border border-blue-100" data-id="element-456">
                      Incluye costo del auto, importación, impuestos,
                      reparaciones y costos de circulación
                    </p>
                    <div className="px-4 py-8" data-id="element-457">
                      <div className="text-center mb-8" data-id="element-458">
                        <span className="text-4xl font-bold text-blue-600" data-id="element-459">
                          ${filters.budgetMin.toLocaleString()} - $
                          {filters.budgetMax.toLocaleString()}
                        </span>
                      </div>

                      <DualRangeSlider min={MIN_BUDGET_LIMIT} max={MAX_BUDGET_LIMIT} step={100} value={[filters.budgetMin, filters.budgetMax]} onChange={([min, max]) => setFilters(prev => ({
                    ...prev,
                    budgetMin: min,
                    budgetMax: max
                  }))} formatLabel={val => `$${val.toLocaleString()}`} inputPrefix="$" data-id="element-460" />

                      <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200 text-center" data-id="element-461">
                        <p className="text-gray-700 font-medium" data-id="element-462">
                          {getBudgetDescription(filters.budgetMin, filters.budgetMax)}
                        </p>
                      </div>
                    </div>
                  </div>}

                {/* STEP 2: YEAR */}
                {step === 'year' && <div className="space-y-8" data-id="element-463">
                    <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-464">
                      <Calendar className="text-blue-600" data-id="element-465" />
                      ¿Qué rango de años buscas?
                    </h2>
                    <p className="text-sm text-gray-500 bg-amber-50 p-3 rounded-lg border border-amber-200" data-id="element-466">
                      Recuerda: Solo se permiten vehículos de {MIN_LEGAL_YEAR}{' '}
                      en adelante.
                    </p>

                    <div className="px-4 py-8" data-id="element-467">
                      <div className="text-center mb-8" data-id="element-468">
                        <span className="text-4xl font-bold text-blue-600" data-id="element-469">
                          {filters.yearMin} - {filters.yearMax}
                        </span>
                      </div>

                      <DualRangeSlider min={MIN_LEGAL_YEAR} max={CURRENT_YEAR} step={1} value={[filters.yearMin, filters.yearMax]} onChange={([min, max]) => setFilters(prev => ({
                    ...prev,
                    yearMin: min,
                    yearMax: max
                  }))} formatLabel={val => val.toString()} data-id="element-470" />
                    </div>
                  </div>}

                {/* STEP 3: TYPE */}
                {step === 'type' && <div className="space-y-6" data-id="element-471">
                    <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-472">
                      <Car className="text-blue-600" data-id="element-473" />
                      ¿Qué tipo de vehículo prefieres?
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-474">
                      Puedes seleccionar varios.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-475">
                      {TYPE_OPTIONS.map(type => <button key={type} onClick={() => toggleType(type)} className={`p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${filters.types.includes(type) ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`} data-id="element-476">
                          <span className={`font-medium ${filters.types.includes(type) ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-477">
                            {type}
                          </span>
                          {filters.types.includes(type) && <Check className="text-blue-600 w-5 h-5" data-id="element-478" />}
                        </button>)}
                    </div>
                  </div>}

                {/* STEP 4: MAKES */}
                {step === 'makes' && <div className="space-y-6" data-id="element-479">
                    <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-480">
                      <Filter className="text-blue-600" data-id="element-481" />
                      ¿Marcas preferidas?
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-482">
                      Selecciona tus favoritas o déjalo vacío para ver todas.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-483">
                      {MAKE_OPTIONS.map(make => <button key={make} onClick={() => toggleMake(make)} className={`py-3 px-4 rounded-xl border-2 transition-all text-sm font-medium ${filters.makes.includes(make) ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-md' : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-gray-50'}`} data-id="element-484">
                          {make}
                        </button>)}
                    </div>
                  </div>}

                {/* STEP 5: RESULTS */}
                {step === 'results' && <div className="space-y-6" data-id="element-485">
                    <div className="text-center" data-id="element-486">
                      <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-487">
                        Resultados Encontrados
                      </h2>
                      <p className="text-gray-600" data-id="element-488">
                        {results.length} vehículos coinciden con tu búsqueda.
                      </p>
                    </div>

                    {/* Sorting and Filtering Controls */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4" data-id="element-489">
                      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-490">
                        <div className="flex items-center gap-2" data-id="element-491">
                          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showFilters ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-492">
                            <SlidersHorizontal className="w-4 h-4" data-id="element-493" />
                            Filtros
                          </button>
                        </div>

                        <div className="flex items-center gap-2" data-id="element-494">
                          <span className="text-sm text-gray-500 hidden sm:inline" data-id="element-495">
                            Ordenar por:
                          </span>
                          <div className="flex bg-white rounded-lg border border-gray-300 p-1" data-id="element-496">
                            <button onClick={() => setSortBy('price')} className={`px-3 py-1 rounded text-sm font-medium transition-colors ${sortBy === 'price' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-497">
                              Precio
                            </button>
                            <button onClick={() => setSortBy('year')} className={`px-3 py-1 rounded text-sm font-medium transition-colors ${sortBy === 'year' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-498">
                              Año
                            </button>
                          </div>
                          <button onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600" title={sortDirection === 'asc' ? 'Ascendente' : 'Descendente'} data-id="element-499">
                            {sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" data-id="element-500" /> : <ArrowDown className="w-4 h-4" data-id="element-501" />}
                          </button>
                        </div>
                      </div>

                      {/* Collapsible Filters */}
                      <AnimatePresence data-id="element-502">
                        {showFilters && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} className="overflow-hidden" data-id="element-503">
                            <div className="pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-504">
                              <div data-id="element-505">
                                <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-506">
                                  Rango de Precio: $
                                  {filters.budgetMin.toLocaleString()} - $
                                  {filters.budgetMax.toLocaleString()}
                                </label>
                                <DualRangeSlider min={MIN_BUDGET_LIMIT} max={MAX_BUDGET_LIMIT} step={100} value={[filters.budgetMin, filters.budgetMax]} onChange={([min, max]) => setFilters(prev => ({
                            ...prev,
                            budgetMin: min,
                            budgetMax: max
                          }))} formatLabel={val => `$${(val / 1000).toFixed(0)}k`} inputPrefix="$" data-id="element-507" />
                              </div>
                              <div data-id="element-508">
                                <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-509">
                                  Rango de Años: {filters.yearMin} -{' '}
                                  {filters.yearMax}
                                </label>
                                <DualRangeSlider min={MIN_LEGAL_YEAR} max={CURRENT_YEAR} step={1} value={[filters.yearMin, filters.yearMax]} onChange={([min, max]) => setFilters(prev => ({
                            ...prev,
                            yearMin: min,
                            yearMax: max
                          }))} formatLabel={val => val.toString()} data-id="element-510" />
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>

                    {results.length > 0 ? <div className="grid grid-cols-1 gap-6" data-id="element-511">
                        {results.map(car => {
                    const years = getYearsFromRange(car.yearRange);
                    const representativeYear = years[Math.floor(years.length / 2)];
                    // Clamp displayed price range to user's budget
                    const displayMin = Math.max(car.priceRange.min, filters.budgetMin);
                    const displayMax = Math.min(car.priceRange.max, filters.budgetMax);
                    return <div key={car.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col sm:flex-row" data-id="element-512">
                              <div className="sm:w-64 h-48 sm:h-auto relative flex-shrink-0 overflow-hidden" data-id="element-513">
                                {renderCarImage(car.make, car.model, representativeYear, car.id, 'w-full h-full')}
                                <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm font-medium" data-id="element-514">
                                  {car.yearRange}
                                </div>
                              </div>
                              <div className="p-4 sm:p-6 flex-1 flex flex-col justify-between" data-id="element-515">
                                <div data-id="element-516">
                                  <div className="flex justify-between items-start mb-2 gap-2" data-id="element-517">
                                    <div data-id="element-518">
                                      <h3 className="text-lg sm:text-xl font-bold text-gray-900" data-id="element-519">
                                        {car.make} {car.model}
                                      </h3>
                                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium mt-1" data-id="element-520">
                                        {car.type}
                                      </span>
                                    </div>
                                    <div className="text-right flex-shrink-0" data-id="element-521">
                                      <p className="text-xs sm:text-sm text-gray-500" data-id="element-522">
                                        Rango estimado
                                      </p>
                                      <p className="text-base sm:text-lg font-bold text-green-600" data-id="element-523">
                                        ${displayMin.toLocaleString()} - $
                                        {displayMax.toLocaleString()}
                                      </p>
                                    </div>
                                  </div>
                                  <p className="text-gray-600 text-sm mb-4 hidden sm:block" data-id="element-524">
                                    {car.description}
                                  </p>
                                  <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-525">
                                    <span className="flex items-center gap-1" data-id="element-526">
                                      <Calendar className="w-4 h-4" data-id="element-527" />{' '}
                                      {car.yearRange}
                                    </span>
                                    <span className="text-gray-400" data-id="element-528">·</span>
                                    <span data-id="element-529">{car.engine}</span>
                                  </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end" data-id="element-530">
                                  <button onClick={() => handleSelectCar(car)} className="text-blue-600 font-bold hover:text-blue-800 text-sm flex items-center gap-1" data-id="element-531">
                                    Cotizar este modelo{' '}
                                    <ChevronRight className="w-4 h-4" data-id="element-532" />
                                  </button>
                                </div>
                              </div>
                            </div>;
                  })}
                      </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300" data-id="element-533">
                        <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-534" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-535">
                          No encontramos coincidencias exactas
                        </h3>
                        <p className="text-gray-500 mb-6" data-id="element-536">
                          Intenta ajustar tu presupuesto o ser menos específico
                          con los filtros.
                        </p>
                        <button onClick={() => {
                    setFilters(INITIAL_FILTERS);
                    setStep('budget');
                  }} className="text-blue-600 font-bold hover:underline" data-id="element-537">
                          Reiniciar búsqueda
                        </button>
                      </div>}
                  </div>}

                {/* STEP 5.5: SELECT YEAR */}
                {step === 'selectYear' && selectedCar && <div className="space-y-6" data-id="element-538">
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-539">
                      <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0" data-id="element-540">
                        {renderCarImage(selectedCar.make, selectedCar.model, getYearsFromRange(selectedCar.yearRange)[Math.floor(getYearsFromRange(selectedCar.yearRange).length / 2)], `preview-${selectedCar.id}`, 'w-full h-full')}
                      </div>
                      <div data-id="element-541">
                        <h3 className="font-bold text-gray-900" data-id="element-542">
                          {selectedCar.make} {selectedCar.model}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-543">
                          {selectedCar.type} · {selectedCar.yearRange}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-544">
                      {getYearsFromRange(selectedCar.yearRange).filter(yr => {
                    if (yr < MIN_LEGAL_YEAR) return false;
                    // Only show years where the minimum estimated price fits the budget
                    const priceEst = estimatePriceForYear(selectedCar, yr);
                    return priceEst.min <= filters.budgetMax;
                  }).reverse().map(yr => {
                    const priceEst = estimatePriceForYear(selectedCar, yr);
                    // Clamp the displayed max price to the user's budget
                    const displayMax = Math.min(priceEst.max, filters.budgetMax);
                    return <button key={yr} onClick={() => handleSelectYear(yr)} className="group relative flex items-center p-5 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50/30 transition-all text-left bg-white shadow-sm hover:shadow-md" data-id="element-545">
                              <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 mr-4" data-id="element-546">
                                {renderCarImage(selectedCar.make, selectedCar.model, yr, `year-${selectedCar.id}-${yr}`, 'w-full h-full')}
                              </div>
                              <div className="flex-1" data-id="element-547">
                                <div className="flex items-center justify-between" data-id="element-548">
                                  <span className="text-2xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors" data-id="element-549">
                                    {yr}
                                  </span>
                                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600" data-id="element-550" />
                                </div>
                                <p className="text-sm font-medium text-green-600 mt-1" data-id="element-551">
                                  ${priceEst.min.toLocaleString()} - $
                                  {displayMax.toLocaleString()}
                                </p>
                              </div>
                            </button>;
                  })}
                    </div>
                  </div>}

                {/* STEP 6: TRIM SELECTION */}
                {step === 'trim' && selectedCar && <div className="space-y-6" data-id="element-552">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-id="element-553">
                      <DollarSign className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-554" />
                      <p className="text-xs text-amber-800" data-id="element-555">
                        Los precios son estimados. Versiones ligeramente fuera
                        de tu presupuesto (hasta <strong data-id="element-556">+$500</strong>) se
                        muestran como opciones cercanas que podrían estar a tu
                        alcance.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-4" data-id="element-557">
                      {selectedCar.trims.filter(trim => {
                    // Show trims whose minimum price is within budget + $500 tolerance
                    const trimMinPrice = calculateTotalCost(trim.auctionEstimate[0]);
                    return trimMinPrice <= filters.budgetMax + 500;
                  }).map((trim, index) => {
                    const trimMin = calculateTotalCost(trim.auctionEstimate[0]);
                    const trimMax = calculateTotalCost(trim.auctionEstimate[1]);
                    // Check if this trim is within tolerance (over budget but within $500)
                    const isWithinTolerance = trimMin > filters.budgetMax;
                    return <button key={index} onClick={() => handleSelectTrim(trim)} className={`group relative flex flex-col sm:flex-row items-start sm:items-center p-6 rounded-xl border-2 transition-all text-left shadow-sm hover:shadow-md ${isWithinTolerance ? 'border-amber-300 bg-amber-50/40 hover:border-amber-500 hover:bg-amber-50/60' : 'border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/30'}`} data-id="element-558">
                              {isWithinTolerance && <div className="absolute -top-3 left-4 bg-amber-500 text-white text-xs font-bold px-2.5 py-0.5 rounded-full" data-id="element-559">
                                  Cerca de tu presupuesto
                                </div>}
                              <div className="flex-1" data-id="element-560">
                                <div className="flex items-center justify-between mb-2 gap-2" data-id="element-561">
                                  <h3 className={`text-xl font-bold transition-colors ${isWithinTolerance ? 'text-amber-900 group-hover:text-amber-700' : 'text-gray-900 group-hover:text-blue-700'}`} data-id="element-562">
                                    {trim.name}
                                  </h3>
                                  <div className="text-right flex-shrink-0" data-id="element-563">
                                    <span className={`block text-base sm:text-lg font-bold px-3 py-1 rounded-lg border ${isWithinTolerance ? 'text-amber-700 bg-amber-100 border-amber-200' : 'text-green-600 bg-green-50 border-green-100'}`} data-id="element-564">
                                      ${trimMin.toLocaleString()} - $
                                      {trimMax.toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3" data-id="element-565">
                                  {trim.features.map((feature, i) => <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-566">
                                      {feature}
                                    </span>)}
                                </div>
                              </div>
                              <div className="mt-4 sm:mt-0 sm:ml-6" data-id="element-567">
                                <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${isWithinTolerance ? 'bg-amber-100 group-hover:bg-amber-500' : 'bg-blue-100 group-hover:bg-blue-600'}`} data-id="element-568">
                                  <ChevronRight className={`w-6 h-6 ${isWithinTolerance ? 'text-amber-600 group-hover:text-white' : 'text-blue-600 group-hover:text-white'}`} data-id="element-569" />
                                </div>
                              </div>
                            </button>;
                  })}
                    </div>
                  </div>}

                {/* STEP 7: CONTACT FORM */}
                {step === 'contact' && selectedCar && selectedTrim && selectedYear && <div className="space-y-6" data-id="element-570">
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-6" data-id="element-571">
                        <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-3" data-id="element-572">
                          Resumen de tu selección
                        </h3>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-573">
                          <div className="flex items-center gap-4" data-id="element-574">
                            <div className="w-20 h-20 rounded-lg overflow-hidden border border-blue-200 flex-shrink-0" data-id="element-575">
                              {renderCarImage(selectedCar.make, selectedCar.model, selectedYear, `contact-${selectedCar.id}`, 'w-full h-full')}
                            </div>
                            <div data-id="element-576">
                              <p className="text-xl font-bold text-gray-900" data-id="element-577">
                                {selectedYear} {selectedCar.make}{' '}
                                {selectedCar.model}
                              </p>
                              <p className="text-gray-600" data-id="element-578">
                                Versión:{' '}
                                <span className="font-semibold" data-id="element-579">
                                  {selectedTrim.name}
                                </span>
                              </p>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-580">
                            <p className="text-sm text-gray-500" data-id="element-581">
                              Estimado total
                            </p>
                            <p className="text-xl font-mono font-bold text-green-600" data-id="element-582">
                              {selectedTrim.priceEstimate}
                            </p>
                          </div>
                        </div>
                      </div>

                      <form onSubmit={handleContactSubmit} className="space-y-4" data-id="element-583">
                        <div data-id="element-584">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-585">
                            Nombre Completo
                          </label>
                          <div className="relative" data-id="element-586">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-587">
                              <User className="h-5 w-5 text-gray-400" data-id="element-588" />
                            </div>
                            <input type="text" id="name" required value={contactForm.name} onChange={e => setContactForm({
                        ...contactForm,
                        name: e.target.value
                      })} className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-3 border" placeholder="Ej. Juan Pérez" data-id="element-589" />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-590">
                          <div data-id="element-591">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-592">
                              Correo Electrónico
                            </label>
                            <div className="relative" data-id="element-593">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-594">
                                <Mail className="h-5 w-5 text-gray-400" data-id="element-595" />
                              </div>
                              <input type="email" id="email" required value={contactForm.email} onChange={e => setContactForm({
                          ...contactForm,
                          email: e.target.value
                        })} className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-3 border" placeholder="juan@ejemplo.com" data-id="element-596" />
                            </div>
                          </div>
                          <div data-id="element-597">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-598">
                              Teléfono / WhatsApp
                            </label>
                            <div className="relative" data-id="element-599">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-600">
                                <Phone className="h-5 w-5 text-gray-400" data-id="element-601" />
                              </div>
                              <input type="tel" id="phone" required value={contactForm.phone} onChange={e => setContactForm({
                          ...contactForm,
                          phone: e.target.value
                        })} className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-3 border" placeholder="+503 7000-0000" data-id="element-602" />
                            </div>
                          </div>
                        </div>

                        <div data-id="element-603">
                          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-604">
                            Mensaje Adicional (Opcional)
                          </label>
                          <div className="relative" data-id="element-605">
                            <div className="absolute top-3 left-3 pointer-events-none" data-id="element-606">
                              <MessageSquare className="h-5 w-5 text-gray-400" data-id="element-607" />
                            </div>
                            <textarea id="message" rows={4} value={contactForm.message} onChange={e => setContactForm({
                        ...contactForm,
                        message: e.target.value
                      })} className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-3 border" placeholder="¿Tienes alguna pregunta específica sobre este modelo?" data-id="element-608" />
                          </div>
                        </div>

                        <button type="submit" disabled={isSubmitting} className={`w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-lg shadow-sm text-lg font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-1 hover:shadow-lg'}`} data-id="element-609">
                          {isSubmitting ? 'Enviando...' : <>
                              <Send className="w-5 h-5 mr-2" data-id="element-610" />
                              Solicitar Cotización
                            </>}
                        </button>

                        <p className="text-xs text-center text-gray-500 mt-4" data-id="element-611">
                          Al enviar este formulario, aceptas ser contactado por
                          EARIA para dar seguimiento a tu solicitud.
                        </p>
                      </form>
                    </div>}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons for filter steps */}
            {genericHeaderSteps.includes(step) && <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between" data-id="element-612">
                {step !== 'budget' ? <button onClick={prevStep} className="flex items-center text-gray-600 font-medium hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-613">
                    <ChevronLeft className="w-5 h-5 mr-1" data-id="element-614" /> Anterior
                  </button> : <div data-id="element-615"></div>}
                <button onClick={nextStep} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg shadow-blue-600/20 flex items-center transition-all transform hover:-translate-y-0.5" data-id="element-616">
                  {step === 'makes' ? 'Ver Resultados' : 'Siguiente'}
                  <ChevronRight className="w-5 h-5 ml-1" data-id="element-617" />
                </button>
              </div>}

            {/* Back buttons for selection steps */}
            {(step === 'selectYear' || step === 'trim' || step === 'contact') && <div className="mt-10 pt-6 border-t border-gray-100" data-id="element-618">
                <button onClick={prevStep} className="flex items-center text-gray-600 font-medium hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-619">
                  <ChevronLeft className="w-5 h-5 mr-1" data-id="element-620" /> Volver
                </button>
              </div>}

            {step === 'results' && <div className="mt-10 text-center" data-id="element-621">
                <button onClick={() => {
              setFilters(INITIAL_FILTERS);
              setStep('budget');
            }} className="text-gray-500 hover:text-gray-900 font-medium underline" data-id="element-622">
                  Realizar otra búsqueda
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}